import pqGuardDogLogo from '@/assets/logo.png';
import { api } from "@/lib/api";
import { AddGuardAPNSubscriptionRequest, AddGuardNameSubscriptionRequest, GuardRecordSubscriptionType, guardRecordSubscriptionType } from "@/types";
import { ActionIcon, Alert, Anchor, Box, Button, Center, Grid, Group, Image, LoadingOverlay, Modal, Paper, Space, Stack, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { formRootRule, useForm } from '@mantine/form';
import { useDisclosure } from "@mantine/hooks";
import { modals } from '@mantine/modals';
import { notifications } from "@mantine/notifications";
import { IconCheck, IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router";
import { useAuth } from "../Auth";
import CountySelect, { CountyMask } from "../CountySelect";
import GuardNameModule from "../GuardNameModule";
import GuardPropertyModule from "../GuardPropertyModule";


const Register: React.FC = () => {
    const location = useLocation();

    // scroll to top of page after a page transition.
    useLayoutEffect(() => {
        document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [location.pathname]);
    useEffect(() => {
        document.title = "Register - Guard Dog Real Estate Protection Service";
    }, []);

    // const [disclaimerOpened, setDisclaimerOpened] = useState(true);
    const [modalOpened, modalHandlers] = useDisclosure(false)
    const [selectedGuardRecordSubscriptionType, setSelectedGuardRecordSubscriptionType] = useState<GuardRecordSubscriptionType>(guardRecordSubscriptionType.apn);
    const [county, setCounty] = useState<CountyMask | null>(null);
    const [guardedNames, setGuardedNames] = useState<{ county: string, name: string }[]>([]);
    const [guardedProperties, setGuardedProperties] = useState<{ county: string, apn: string, description?: string }[]>([]);
    const [tempGuardedNames, setTempGuardedNames] = useState<{ county: string, name: string }[]>([]);
    const [tempGuardedProperties, setTempGuardedProperties] = useState<{ county: string, apn: string, description?: string }[]>([]);
    const { loading } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const form = useForm({
        mode: 'uncontrolled',
        validateInputOnChange: true,
        initialValues: {
            user: {
                email: '',
                confirmEmail: '',
                apns: Array<AddGuardAPNSubscriptionRequest>(),
                names: Array<AddGuardNameSubscriptionRequest>(),
            }
        },
        validate: {
            user: {
                [formRootRule]: (value) =>
                    value.apns.length === 0 && value.names.length === 0
                        ? 'At least one guard record subscription is required' : null,
                email: (value) => value.trim().length === 0 ? 'Email is required' : (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
                confirmEmail: (value, values) => value.trim().length === 0 ? 'Confirm Email is required' : (value !== values.user.email ? 'Emails do not match' : null),
            },
        }
    })
    const onAddGuardRecordSubscriptions = (guardRecordSubscriptionType: GuardRecordSubscriptionType) => {
        setSelectedGuardRecordSubscriptionType(guardRecordSubscriptionType);
        setTempGuardedNames([...guardedNames]);
        setTempGuardedProperties([...guardedProperties]);
        modalHandlers.open();
    }
    const onAddGuardName = (guardRecordSubscription: { county: string, name: string }) => {
        setTempGuardedNames([...tempGuardedNames, { county: guardRecordSubscription.county, name: guardRecordSubscription.name.trim() }]);
    }

    const onAddGuardProperty = (guardRecordSubscription: { county: string, apn: string, description?: string }) => {
        setTempGuardedProperties([...tempGuardedProperties, { county: guardRecordSubscription.county, apn: guardRecordSubscription.apn.trim(), description: guardRecordSubscription.description }]);
    }
    useEffect(() => {
        form.setFieldValue('user.names', [...guardedNames]);
    }, [guardedNames]);

    useEffect(() => {
        form.setFieldValue('user.apns', [...guardedProperties]);
    }, [guardedProperties]);

    const onFormError = () => {
        if (form.errors.user) {
            notifications.show({
                title: 'Error',
                message: form.errors.user,
                position: 'top-center',
                color: 'red',
                autoClose: false,
                "aria-live": "polite",
                closeButtonProps: {
                    "aria-label": "Close error notification"
                }
            })
        }
        setIsSubmitting(false);
    }
    const onFormSuccess = async (values: any) => {
        const { confirmEmail, ...onSubmitValues } = values.user;
        setIsSubmitting(true);
        try {
            const data = await api.post('/user/register', onSubmitValues);
            if (data) {
                notifications.show({
                    title: 'Success',
                    message: 'User registered successfully',
                    position: 'top-center',
                    icon: <IconCheck />,
                    color: 'green',
                    "aria-live": "polite",
                    closeButtonProps: {
                        "aria-label": "Close success notification"
                    }
                })
                setShowSuccess(true);
                // setDisclaimerOpened(false);
                form.reset();
                return;
            }
        }
        catch (error: any) {
            if (error.response?.status === 409) {
                notifications.show({
                    title: 'Error',
                    message: 'User with this email already exists',
                    position: 'top-center',
                    color: 'red',
                    autoClose: false,
                    "aria-live": "polite",
                    closeButtonProps: {
                        "aria-label": "Close error notification"
                    }
                });
            }
        }
        setIsSubmitting(false);
    }
    const onAddGuardSubscriptionModalClose = () => {
        if(guardedNames.length !== tempGuardedNames.length || guardedProperties.length !== tempGuardedProperties.length) {
            modals.openConfirmModal({
                title: 'Discard Changes?',
                centered: true,
                children: <Text>You are about to exit this screen without guarding your selected items. Are you sure?</Text>,
                labels: { confirm: 'Discard Changes', cancel: 'Keep Adding' },
                confirmProps: { color: 'pq-red' },
                onCancel: () => { },
                onConfirm: () => {
                    setTempGuardedNames([...guardedNames]);
                    setTempGuardedProperties([...guardedProperties]);
                    modalHandlers.close();
                }
            });
        } else {
            modalHandlers.close();
      }
    }

    const onAddGuardSubscriptionModalConfirm = () => {
        setGuardedNames([...tempGuardedNames]);
        setGuardedProperties([...tempGuardedProperties]);
        modalHandlers.close();
    }
    const onRemoveGuardRecordSubscription = (type: GuardRecordSubscriptionType, index: number) => {
        const { user } = form.getValues();
        switch (type) {
            case guardRecordSubscriptionType.apn:
                const properties = user.apns.length > 1 ? user.apns.filter((_, i) => i !== index) : [];
                setGuardedProperties([...properties]);
                form.setFieldValue('user.apns', [...properties]);
                break;
            case guardRecordSubscriptionType.name:
                const names = user.names.length > 1 ? user.names.filter((_, i) => i !== index) : [];
                setGuardedNames([...names]);
                form.setFieldValue('user.names', [...names]);
                break;
        }
    }

    return (
        <>
            <Stack>
                <Stack align='center' justify='center' mb={80}>
                <Anchor href='/'>
                    <Image style={{objectFit: 'contain'}} src={pqGuardDogLogo} alt='Guard Dog Service' height={120} />
                </Anchor>
                <Title style={{ textAlign: 'center', fontFamily: 'Elston, sans-serif', letterSpacing: '2px' }} order={1}>Free<br />Real Estate Protection Service</Title>
            </Stack>
                {showSuccess && (
                    <Center >
                        <Alert styles={{ title: { fontSize: '20px', 'fontFamily': 'Elston' } }} color='green' title='User registered successfully' icon={<IconCheck />} >
                            <Title order={3} mb={10}>Thank you for registering for Guard Dog&trade; Real Estate Protection Service!</Title>
                            <Text fz={20}>
                                Please check your email to verify your account.
                            </Text>
                        </Alert>
                    </Center>
                )}

                {!showSuccess && <form onSubmit={form.onSubmit(onFormSuccess, onFormError)}>
                    <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} loaderProps={{ color: 'pq-red' }} />
                    <Grid>
                        <Grid.Col span={12}>
                            <Space h='md' />
                            <Group align="center" justify="start" gap={7}>
                                <Text style={{textTransform:'uppercase'}} fz='h5' fw='bold' c='pq-red'>Step 1:</Text>
                                <Text fz='h5'>Enter the email address where you would like to receive Guard Dog notifications.</Text>
                            </Group>
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                            <TextInput mx='md' data-autofocus  {...form.getInputProps('user.email')} label='Email' placeholder='Enter your email' type="email" />
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                            <TextInput mx='md' {...form.getInputProps('user.confirmEmail')} label='Confirm Email' placeholder='Confirm your Email' type="email" />
                        </Grid.Col>
                        <Grid.Col span={12}>
                            <Space h='md' />
                             <Group align="center" justify="start" gap={7}>
                                <Text style={{textTransform:'uppercase'}} fz='h5' fw='bold' c='pq-red'>Step 2:</Text>
                                <Text fz='h5'>Select the owner names and/or properties to be guarded.</Text>
                            </Group>
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                            <Paper mx='md' shadow="sm" p='md' radius='md' withBorder>
                                <Stack align="start" justify="space-between">
                                    <Group tabIndex={0} role="button" w='100%' onClick={() => onAddGuardRecordSubscriptions(guardRecordSubscriptionType.name)} align="center" justify="space-between">
                                        <Title order={6}>Guard a Name</Title>
                                        <ActionIcon aria-label="Add guard name subscription" radius='xl' size='sm' color="pq-red"><IconPlus /></ActionIcon>
                                    </Group>
                                </Stack>
                            </Paper>
                            {form.getValues().user.names.length > 0 && <Paper mx='md' shadow="sm" p='md' mt={20} radius='md' withBorder>
                                <Stack align="start" justify="space-between">
                                    <Title order={6}>Names To Be Guarded</Title>

                                    {form.getValues().user.names.map((guardNameSubscription, index) => (
                                        <Group w='100%' align="center" justify="space-between" key={index}>
                                            <Box w='80%'>
                                                <Text fz='xs'>County: {guardNameSubscription.county}</Text>
                                                <Tooltip label={guardNameSubscription.name} position='top' withArrow>
                                                    <Text truncate fz='xs'>Name: {guardNameSubscription.name}</Text>
                                                </Tooltip>
                                            </Box>
                                            <ActionIcon aria-label="Remove guard name subscription" onClick={() => onRemoveGuardRecordSubscription(guardRecordSubscriptionType.name, index)} radius='xl' size='sm' color="pq-red"><IconTrash size={15} /></ActionIcon>
                                        </Group>
                                    ))}
                                </Stack>
                            </Paper>}
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                            <Paper mx='md' shadow="sm" p='md' radius='md' withBorder>
                                <Stack align="start" justify="space-between">
                                    <Group tabIndex={0} role="button" w='100%' onClick={() => onAddGuardRecordSubscriptions(guardRecordSubscriptionType.apn)} align="center" justify="space-between">
                                        <Title order={6}>Guard a Property</Title>
                                        <ActionIcon aria-label="Add guard property subscription" radius='xl' size='sm' color="pq-red"><IconPlus /></ActionIcon>
                                    </Group>
                                </Stack>
                            </Paper>
                            {form.getValues().user.apns.length > 0 && <Paper mx='md' shadow="sm" p='md' mt={20} radius='md' withBorder>
                                <Stack align="start" justify="space-between">
                                    <Title order={6}>Properties To Be Guarded</Title>
                                    {form.getValues().user.apns.map((guardNameSubscription, index) => (
                                        <Group w='100%' align="center" justify="space-between" key={index}>
                                            <Box w='80%'>
                                                <Text fz='xs'>County: {guardNameSubscription.county}</Text>
                                                <Tooltip label={guardNameSubscription.apn} position='top' withArrow>
                                                    <Box>
                                                        <Text truncate fz='xs'>APN: {guardNameSubscription.apn}</Text>
                                                        <Text truncate fz='xs'>Address: {guardNameSubscription.description ?? 'Unknown'}</Text>
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                            <ActionIcon aria-label="Remove guard property subscription" onClick={() => onRemoveGuardRecordSubscription(guardRecordSubscriptionType.apn, index)} radius='xl' size='sm' color="pq-red"><IconTrash size={15} /></ActionIcon>
                                        </Group>
                                    ))}
                                </Stack>
                            </Paper>}
                        </Grid.Col>
                        <Grid.Col span={12}>
                            <Space h='md' />
                            <Group align="center" justify="start" gap={7}>
                                <Text style={{textTransform:'uppercase'}} fz='h5' fw='bold' c='pq-red'>Step 3:</Text>
                                <Text fz='h5'>When you are finished adding items to register, click “SIGN UP.”</Text>
                            </Group>
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                            <Button mx='md' disabled={isSubmitting} loading={isSubmitting} type='submit' color="pq-red" fullWidth>SIGN UP</Button>
                        </Grid.Col>
                        <Grid.Col span={12}  mb='80px'>
                            <Text mx='md' fz='14px'>
                                By clicking the "Sign Up" button you agree to Guard Dog's <a target="_blank" href='/terms'>Terms of Use</a> and <a target="_blank" href='/privacy'>Privacy Policy</a> and consent to receive email notifications related to your account, the service, the items you have identified for guarding, and other relevant communications.
                            </Text>
                        </Grid.Col>
                    </Grid>
                </form>}
                {/* {!showSuccess && <Alert color='pq-red' variant="light" hidden={!disclaimerOpened} title='Guard Dog&trade; Service Disclaimer' withCloseButton={true} onClose={() => setDisclaimerOpened(false)} icon={<IconInfoCircle />}>
                    <Text fz='sm'>
                        ParcelQuest does not represent or endorse the accuracy or reliability of any of the information contained in this service. <br />
                        <br />
                        ParcelQuest reserves the right, in its sole discretion and without any obligation, to make improvements or correct any errors or omissions in any portion of the service. <br />
                        <br />
                        Assessing the accuracy and reliability of the information is the responsibility of the user. <br />
                        <br />
                        ParcelQuest shall not be liable for errors or for any damages in connection with the use of the information contained herein. Names and E-mail addresses used for Guard Dog&trade; may be subject to disclosure under the California Public Records Act.
                    </Text>
                </Alert>} */}
            </Stack>
            <Modal size={'xl'} withCloseButton={true} opened={modalOpened} onClose={onAddGuardSubscriptionModalClose} title={`Guard A ${selectedGuardRecordSubscriptionType}`}>
                <Stack>
                    <Title order={6}>Step 1: Select a County</Title>
                    <CountySelect value={county?.name} onChange={(county) => setCounty(county)} />
                    {
                        county && selectedGuardRecordSubscriptionType === guardRecordSubscriptionType.apn && (
                            <>
                                <GuardPropertyModule
                                    key={modalOpened ? 'guardPropertyModule' : ''}
                                    selectedCounty={county}
                                    onGuardProperty={onAddGuardProperty}
                                    guardedCountyProperties={tempGuardedProperties}
                                />
                            </>
                        )
                    }
                    {
                        county && selectedGuardRecordSubscriptionType === guardRecordSubscriptionType.name && (

                            <GuardNameModule
                                key={modalOpened ? 'guardNameModule' : ''}
                                selectedCounty={county}
                                onGuardName={onAddGuardName}
                                guardedCountyNames={tempGuardedNames}
                            />

                        )
                    }
                    <Group justify='flex-end'>
                        <Button color='pq-red' onClick={onAddGuardSubscriptionModalConfirm}>Guard Selected Items</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}

export default Register;